import $ from 'jquery';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {applyTimezone, dateFormat, translate} from '../utils';

export default function DateInput({value, showTodayButton = false, removeButton = false, pluginOptions = null, readOnly = true, ...inputProps}) {
    const inputEl = useRef();
    const plugin = useRef();
    const onChange = useRef();

    useEffect(() => {
        onChange.current = inputProps.onChange;
    }, [inputProps.onChange]);

    const datepickerOptions = useMemo(() => {
        return {
            // https://bootstrap-datepicker.readthedocs.io/en/latest/options.html#format
            format: window.config.basicSettings.setting_default_dateformat,
            todayHighlight: true,
            weekStart: 1,
            autoclose: true,
            ...pluginOptions
        };
    }, [pluginOptions]);

    useEffect(() => {
        const $inputEl = $(inputEl.current);
        $inputEl
            .on('changeDate', (e) => onChange.current(e))
            .datepicker(datepickerOptions);
        plugin.current = $inputEl;

        return () => {
            $inputEl
                .off('changeDate')
                .datepicker('destroy');
            plugin.current = null;
        };
    }, [datepickerOptions]);

    const controlledValue = value;
    useEffect(() => {
        const existingDate = plugin.current.datepicker('getDate');
        if (controlledValue) {
            const existingFormattedDate = existingDate ? dateFormat(existingDate) : null;
            if (controlledValue !== existingFormattedDate) {
                // To reduce amount of events triggerred, only sync dates when it is known to be different
                plugin.current.datepicker('setDate', controlledValue);
            }
        } else {
            if (existingDate) {
                // To reduce amount of events triggerred, only sync dates when it is known to be different
                plugin.current.datepicker('clearDates');
            }
        }
    }, [controlledValue]);

    const onAddonClick = useCallback(() => {
        if (!plugin.current) return;

        plugin.current.datepicker('show');
    }, []);

    const onTodayClick = useCallback(() => {
        if (!plugin.current) return;

        plugin.current.datepicker('setDate', dateFormat(applyTimezone(new Date())));
    }, []);

    const onRemoveClick = useCallback(() => {
        if (!plugin.current) return;

        plugin.current.datepicker('clearDates').trigger('change');
    }, []);

    return <div className="input-group">
        {showTodayButton ?
            <span className="input-group-btn">
                <button type="button" className="btn btn-default" onClick={onTodayClick}>
                    <i className="fa fa-calendar-check"/>
                    <span className="hidden-xs"> {translate('frontend', 'TODAY')}</span>
                </button>
            </span>
            : null}
        <input ref={inputEl} readOnly={readOnly} {...inputProps}/>
        {removeButton ?
            <span className="input-group-addon" onClick={onRemoveClick} style={{cursor: 'pointer'}}><i className="fa fa-times"/></span>
            : null}
        <span className="input-group-addon" onClick={onAddonClick} style={{cursor: 'pointer'}}><i className="fa fa-calendar"/></span>
    </div>;
}
